<template>
  <section class="panel" aria-label="Organizations Overview">
    <div class="panel__title">
      Organizations Overview
      <button
        class="panel__link"
        @click="$emit('go-to-orgs')"
      >
        View all →
      </button>
    </div>

    <div class="summary-stats">
      <div class="stat-card">
        <span class="stat-card__number">{{ totalOrgs }}</span>
        <span class="stat-card__label">Total</span>
      </div>
      <div class="stat-card stat-card--green">
        <span class="stat-card__number">{{ licensedOrgs }}</span>
        <span class="stat-card__label">Licensed</span>
      </div>
      <div class="stat-card stat-card--muted">
        <span class="stat-card__number">{{ unlicensedOrgs }}</span>
        <span class="stat-card__label">Unlicensed</span>
      </div>
    </div>

    <div v-if="alerts.length" class="summary-alerts">
      <div
        v-for="(alert, i) in alerts"
        :key="i"
        class="summary-alert"
        :class="'summary-alert--' + alert.level"
      >
        <v-icon x-small :color="alertColor(alert.level)">
          {{ alert.level === 'critical' ? 'error' : 'warning' }}
        </v-icon>
        <span>{{ alert.text }}</span>
      </div>
    </div>

    <div v-else class="summary-healthy">
      <v-icon small color="success">check_circle</v-icon>
      <span>All organizations healthy</span>
    </div>
  </section>
</template>

<script>
import { getOrgMaxUsage } from '@/components/Settings/licensing-constants';

export default {
  name: 'OrgSummaryPanel',
  props: {
    organizations: { type: Array, default: () => [] },
  },
  computed: {
    totalOrgs() {
      return this.organizations.length;
    },
    licensedOrgs() {
      return this.organizations.filter(o => o.licenses && o.licenses.length > 0).length;
    },
    unlicensedOrgs() {
      return this.totalOrgs - this.licensedOrgs;
    },
    alerts() {
      const items = [];
      let expiredCount = 0;
      let expiringCount = 0;
      let cappedCount = 0;
      let highUsageCount = 0;

      this.organizations.forEach(org => {
        const lics = org.licenses || [];
        const hasExpired = lics.some(l => l.isExpired);
        const hasExpiring = lics.some(l => {
          if (l.isExpired || !l.expiresAt) return false;
          const days = Math.ceil((new Date(l.expiresAt).getTime() - Date.now()) / 86400000);
          return days <= 30;
        });
        const maxUsage = this.getOrgMaxUsage(org);

        if (hasExpired) expiredCount += 1;
        if (hasExpiring) expiringCount += 1;
        if (maxUsage >= 100) cappedCount += 1;
        else if (maxUsage >= 75) highUsageCount += 1;
      });

      if (expiredCount) {
        items.push({ level: 'critical', text: `${expiredCount} org${expiredCount > 1 ? 's' : ''} with expired licenses` });
      }
      if (cappedCount) {
        items.push({ level: 'critical', text: `${cappedCount} org${cappedCount > 1 ? 's' : ''} at capacity` });
      }
      if (expiringCount) {
        items.push({ level: 'warning', text: `${expiringCount} org${expiringCount > 1 ? 's' : ''} expiring within 30 days` });
      }
      if (highUsageCount) {
        items.push({ level: 'warning', text: `${highUsageCount} org${highUsageCount > 1 ? 's' : ''} above 75% usage` });
      }
      if (this.unlicensedOrgs > 0) {
        items.push({ level: 'info', text: `${this.unlicensedOrgs} org${this.unlicensedOrgs > 1 ? 's' : ''} without licenses` });
      }
      return items;
    },
  },
  methods: {
    getOrgMaxUsage,
    alertColor(level) {
      if (level === 'critical') return 'error';
      if (level === 'warning') return '#f59e0b';
      return 'grey';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_sizing";
@import "@/assets/styles/_colors";
@import "@/assets/styles/_semantic-sizing";

.panel {
  background: var(--bg-secondary);
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-lg;
  padding: $space-5 $space-6;

  &__title {
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-base;
    margin-bottom: $space-4;
    padding-bottom: $space-2;
    border-bottom: $border-width-thin solid var(--border-primary);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__link {
    background: none;
    border: none;
    color: var(--brand-primary);
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    cursor: pointer;
    padding: $space-1 $space-2;
    border-radius: $radius-md;

    &:hover { text-decoration: underline; }
    &:focus-visible { outline: $border-width-medium solid var(--brand-primary); outline-offset: $border-width-thin; }
  }
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $space-3;
  margin-bottom: $space-4;
}

.stat-card {
  text-align: center;
  padding: $space-3 $space-2;
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-6;
  background: var(--bg-primary);

  &__number {
    display: block;
    font-family: $font-family-bold;
    font-size: $font-size-xl;
    color: var(--text-primary);
    line-height: 1.2;
  }

  &__label {
    display: block;
    font-family: $font-family-regular;
    font-size: $font-size-xs;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: $letter-spacing-wider;
    margin-top: $space-1;
  }

  &--green {
    border-color: rgba($success-500, 0.3);
    .stat-card__number { color: $success-500; }
  }

  &--muted {
    .stat-card__number { color: var(--text-secondary); }
  }
}

.summary-alerts {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.summary-alert {
  display: flex;
  align-items: center;
  gap: $space-2;
  padding: $space-2 $space-3;
  border-radius: $radius-6;
  font-family: $font-family-regular;
  font-size: $font-size-sm;

  &--critical { background: $error-50; color: $error-500; }
  &--warning { background: $warning-50; color: $warning-500; }
  &--info { background: var(--bg-tertiary, $gray-100); color: var(--text-secondary); }
}

.summary-healthy {
  display: flex;
  align-items: center;
  gap: $space-2;
  padding: $space-3;
  border-radius: $radius-6;
  background: $success-50;
  color: $success-500;
  font-family: $font-family-regular;
  font-size: $font-size-sm;
}
</style>
